import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../i18n/useTranslation';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { toast } from 'react-toastify';
import { useQuestionnaire } from '../context/QuestionnaireContext';
import { surveyApi, reportApi, getVisitorId } from '../services/api';
import './ResultsPage.css';

type LevelCss = 'critical' | 'average' | 'good' | 'very-good';

const LEVEL_CSS_ORDER: LevelCss[] = ['critical', 'average', 'good', 'very-good'];

const levelCssToI18nKey = (css: LevelCss): 'critical' | 'average' | 'good' | 'veryGood' => {
  if (css === 'very-good') return 'veryGood';
  return css;
};

/** Report / email API profile UUID per UI language */
function getReportProfileId(language: string): string {
  const base = (language || 'en').split('-')[0];
  if (base === 'tr') return 'a755ed24-b60d-422e-8b69-0678c6c4bbe5';
  if (base === 'de') return '9c4abcda-8713-4d5e-b27d-4582be11c378';
  return '6a35d02d-b716-4b88-bbd6-abee8a28f067';
}

function getRecommendationLanguage(language: string): 'EN' | 'DE' | 'TR' | 'ES' | 'FR' {
  const base = (language || 'en').split('-')[0];
  if (base === 'tr') return 'TR';
  if (base === 'de') return 'DE';
  if (base === 'es') return 'ES';
  if (base === 'fr') return 'FR';
  return 'EN';
}

function getProductDescription(
  englishDescription: unknown,
  fallbackDescription: unknown
): string {
  if (typeof englishDescription === 'string' && englishDescription.trim()) {
    return englishDescription.trim();
  }
  if (typeof fallbackDescription !== 'string') {
    return '';
  }
  const trimmed = fallbackDescription.trim();
  // Ignore routine arrays accidentally sent as description text, e.g. ["Gündüz"]
  if (!trimmed || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
    return '';
  }
  return trimmed;
}

type AnalysisImageKeys = 'original' | 'Acne' | 'DarkCircle' | 'Dryness' | 'Spot' | 'Redness' | 'Wrinkle';

/** Maps skin rating card `nameKey` to stacked overlay image key */
const METRIC_NAME_KEY_TO_OVERLAY: Record<string, AnalysisImageKeys> = {
  acne: 'Acne',
  wrinkles: 'Wrinkle',
  redness: 'Redness',
  darkCircles: 'DarkCircle',
  dryness: 'Dryness',
  spots: 'Spot',
};

// Backend returns Type/Data (PascalCase); support both for robustness
interface AnalysisImageItem {
  type?: string;
  data?: string;
  Type?: string;
  Data?: string;
}

interface AnalysisImages {
  [key: string]: string | undefined;
}

// New AnalysisResults item format (array) – backend now returns:
// [{ type: 'Acne', score: 0.06, level: 'Cok Iyi' }, ...]
// Keep it flexible to support both old (PascalCase) and new (camelCase) shapes.
interface AnalysisResultItem {
  type?: string;
  Type?: string;
  score?: number;
  Score?: number;
  level?: string;
  Level?: string;
}

interface SingleAnalysisResult {
  // Normalized score used for UI (0–100)
  Score: number;
  Level: string;
  // Raw score from backend (typically 0–1), used for API payloads
  RawScore?: number;
}

interface AnalysisResults {
  [key: string]: SingleAnalysisResult | undefined;
}

interface Metric {
  nameKey: string;
  level: LevelCss;
  score: number;
}

interface Product {
  id: string | number;
  name: string;
  brand: string;
  description: string;
  imageUrl?: string;
  imageAlt?: string;
  url?: string;
  routines?: string[];
}

// Report compute API response (healthScore, level, summary, matrix, issues)
interface ReportMatrix {
  Acne?: number;
  Redness?: number;
  Spots?: number;
  Dryness?: number;
  Wrinkles?: number;
  DarkCircles?: number;
}

interface ReportIssue {
  key: string;
  title?: string;
  modelType?: string;
  severity?: string;
  description?: string;
  order?: number;
}

interface ReportData {
  healthScore?: number;
  level?: string;
  summary?: string;
  matrix?: ReportMatrix;
  issues?: ReportIssue[];
  recommendedProducts?: any[] | null;
}

const RESULTS_PAGE_STORAGE_KEY = 'resultsPageState';

// Normalize backend type/modelType strings to canonical keys used in AnalysisResults
// so that "Spots" and "Spot" or "Wrinkles"/"Wrinkle" map consistently.
const normalizeAnalysisKey = (rawKey: string): keyof AnalysisResults => {
  const k = rawKey.toLowerCase();
  if (k === 'acne') return 'Acne';
  if (k === 'redness') return 'Redness';
  if (k === 'dryness') return 'Dryness';
  if (k === 'spots' || k === 'spot') return 'Spot';
  if (k === 'wrinkles' || k === 'wrinkle') return 'Wrinkle';
  if (k === 'darkcircles' || k === 'darkcircle' || k === 'dark_circles') return 'DarkCircle';
  return rawKey as keyof AnalysisResults;
};

// Normalize backend image Type keys to match the ones we use in overlays and base image selection.
// Ensures overlay keys like Spots/Wrinkles/DarkCircles map to Spot/Wrinkle/DarkCircle internally.
const normalizeImageKey = (rawKey: string): AnalysisImageKeys | 'original' => {
  const k = rawKey.toLowerCase();
  if (k === 'original') return 'original';
  if (k === 'acne') return 'Acne';
  if (k === 'redness') return 'Redness';
  if (k === 'dryness') return 'Dryness';
  if (k === 'spots' || k === 'spot') return 'Spot';
  if (k === 'wrinkles' || k === 'wrinkle') return 'Wrinkle';
  if (k === 'darkcircles' || k === 'darkcircle' || k === 'dark_circles') return 'DarkCircle';
  return rawKey as AnalysisImageKeys | 'original';
};

type ResultsPageStoredState = {
  capturedImage?: string;
  analysisImages?: AnalysisImages | AnalysisImageItem[] | null;
  analysisResults?: AnalysisResults | AnalysisResultItem[] | null;
  recommendedProducts?: any[] | null;
  reportData?: ReportData | null;
} | null;

function getInitialResultsState(locationState: unknown): ResultsPageStoredState {
  const loc = locationState as ResultsPageStoredState;
  if (loc && (loc.capturedImage || loc.analysisResults)) {
    return { ...loc, recommendedProducts: loc.recommendedProducts ?? null, reportData: loc.reportData ?? null };
  }
  try {
    const s = sessionStorage.getItem(RESULTS_PAGE_STORAGE_KEY);
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
}

const ResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { surveyAnswersForRecommendations, clearAnswers } = useQuestionnaire();

  const initialState = React.useMemo(
    () => getInitialResultsState(location.state),
    [location.state]
  );

  // Get captured image from location state or restored sessionStorage
  const capturedImage = initialState?.capturedImage || null;
  const analysisImagesRaw = initialState?.analysisImages || null;
  const analysisResultsRaw = initialState?.analysisResults || null;

  // Convert images array to object format if needed
  const analysisImages: AnalysisImages | null = React.useMemo(() => {
    if (!analysisImagesRaw) return null;
    
    // If it's already an object, return as-is
    if (!Array.isArray(analysisImagesRaw)) {
      const imagesObj = { ...(analysisImagesRaw as AnalysisImages) };
      const normalized: AnalysisImages = {};
      Object.entries(imagesObj).forEach(([k, v]) => {
        if (!v) return;
        const nk = normalizeImageKey(k);
        normalized[nk] = v;
      });
      return normalized;
    }
    
    // Convert array format [{Type, Data}] or [{type, data}] to object format {Type: data, ...}
    const imagesArray = analysisImagesRaw as AnalysisImageItem[];
    const imagesObj: AnalysisImages = {};
    imagesArray.forEach((img) => {
      const rawKey = img.Type ?? img.type;
      const value = img.Data ?? img.data;
      if (rawKey && value) {
        const key = normalizeImageKey(rawKey);
        imagesObj[key] = value;
      }
    });
    return imagesObj;
  }, [analysisImagesRaw]);

  const [recommendedProducts, setRecommendedProducts] = useState<any[] | null>(initialState?.recommendedProducts ?? null);
  const [, setIsLoadingRecommendations] = useState(false);
  const [reportData, setReportData] = useState<ReportData | null>(initialState?.reportData ?? null);
  const [reportEmail, setReportEmail] = useState('');
  const [isSendingReportEmail, setIsSendingReportEmail] = useState(false);
  const [openIssueKey, setOpenIssueKey] = useState<string | null>(null);
  /** Overlays listed here are hidden on the face image (toggle via metric cards) */
  const [hiddenOverlayKeys, setHiddenOverlayKeys] = useState<Set<AnalysisImageKeys>>(
    () => new Set()
  );

  // Normalize AnalysisResults to the legacy object format regardless of whether
  // the backend sent an object (old) or an array of items (new).
  const analysisResults: AnalysisResults | null = React.useMemo(() => {
    if (!analysisResultsRaw) return null;

    // If it's an array, convert [{type, score, level}] → { Type: {Score, Level}, ... }
    if (Array.isArray(analysisResultsRaw)) {
      const items = analysisResultsRaw as AnalysisResultItem[];
      const resultObj: AnalysisResults = {};

      items.forEach((item) => {
        const rawKey = item.type ?? item.Type;
        if (!rawKey) return;
        const key = normalizeAnalysisKey(rawKey);

        const rawScore = item.score ?? item.Score;
        if (rawScore == null) return;

        // Backend may send scores as 0–1 or 0–100.
        // For UI we normalize to 0–100, but we also keep a 0–1 raw score for payloads.
        const isFraction = rawScore <= 1;
        const normalizedScore = isFraction ? rawScore * 100 : rawScore;
        const rawForPayload = isFraction ? rawScore : rawScore / 100;
        const levelText = item.Level ?? item.level ?? '';

        resultObj[key] = {
          Score: normalizedScore,
          Level: levelText,
          RawScore: rawForPayload,
        };
      });

      return resultObj;
    }

    // Already in object format – just use it as-is.
    return analysisResultsRaw as AnalysisResults;
  }, [analysisResultsRaw]);

  const getDataUrl = (base?: string) => {
    if (!base) return undefined;
    if (base.startsWith('data:image')) return base;
    // Backend overlay images are often PNG (base64 starts with iV); original is usually JPEG
    const mime = base.startsWith('iV') ? 'image/png' : 'image/jpeg';
    return `data:${mime};base64,${base}`;
  };

  // Use backend's 1024×1024 original as base when we have it so overlays (also 1024×1024) align.
  // Fall back to captured image only when backend original is missing (e.g. no analysis yet).
  const backendOriginal = analysisImages?.original ? getDataUrl(analysisImages.original) : null;
  const baseImageSrc = backendOriginal || capturedImage || null;
  const overlayOrder: AnalysisImageKeys[] = ['Acne', 'DarkCircle', 'Dryness', 'Spot', 'Redness', 'Wrinkle'];

  const toggleOverlayForMetric = useCallback(
    (nameKey: string) => {
      const overlayKey = METRIC_NAME_KEY_TO_OVERLAY[nameKey];
      if (!overlayKey || !analysisImages?.[overlayKey]) return;
      setHiddenOverlayKeys((prev) => {
        const next = new Set(prev);
        if (next.has(overlayKey)) next.delete(overlayKey);
        else next.add(overlayKey);
        return next;
      });
    },
    [analysisImages]
  );

  const handleRestart = () => {
    clearAnswers();
    try {
      sessionStorage.removeItem(RESULTS_PAGE_STORAGE_KEY);
    } catch {
      // ignore
    }
    navigate('/');
  };

  // Helper: get raw (0–1) score for API payloads from a normalized result
  const getRawScoreForPayload = (result?: SingleAnalysisResult): number => {
    if (!result) return 0;
    if (typeof result.RawScore === 'number') return result.RawScore;
    // Fallback: derive 0–1 from 0–100 Score
    return result.Score / 100;
  };

  // Map numeric score (0 best, 100 worst) to CSS band (for styling + i18n key)
  const scoreToLevelCss = (score: number): LevelCss => {
    if (score <= 25) return 'very-good';
    if (score <= 50) return 'good';
    if (score <= 75) return 'average';
    return 'critical';
  };

  // Build metrics from backend AnalysisResults when available
  const metricConfig: { key: keyof AnalysisResults; nameKey: string }[] = [
    { key: 'Acne', nameKey: 'acne' },
    { key: 'Wrinkle', nameKey: 'wrinkles' },
    { key: 'Redness', nameKey: 'redness' },
    { key: 'DarkCircle', nameKey: 'darkCircles' },
    { key: 'Dryness', nameKey: 'dryness' },
    { key: 'Spot', nameKey: 'spots' },
  ];

  const metrics: Metric[] = analysisResults
    ? metricConfig.reduce<Metric[]>((acc, m) => {
        const result = analysisResults[m.key];
        if (!result) {
          return acc;
        }
        acc.push({
          nameKey: m.nameKey,
          level: scoreToLevelCss(result.Score),
          score: result.Score,
        });
        return acc;
      }, [])
    : [
        { nameKey: 'acne', level: 'very-good', score: 0 },
        { nameKey: 'darkCircles', level: 'very-good', score: 5 },
        { nameKey: 'dryness', level: 'very-good', score: 10 },
        { nameKey: 'spots', level: 'very-good', score: 15 },
        { nameKey: 'redness', level: 'good', score: 40 },
        { nameKey: 'wrinkles', level: 'very-good', score: 20 },
      ];

  const averageScore =
    metrics.length > 0
      ? metrics.reduce<number>((sum, m) => sum + m.score, 0) / metrics.length
      : 0;
  const level: LevelCss = scoreToLevelCss(averageScore);

  // Fetch recommended products when we have both analysis results and survey answers
  useEffect(() => {
    const fetchRecommendations = async () => {
      if (!analysisResults || !surveyAnswersForRecommendations || surveyAnswersForRecommendations.length === 0) {
        return;
      }

      try {
        setIsLoadingRecommendations(true);

        const analysisScoresPayload = {
          Acne: getRawScoreForPayload(analysisResults.Acne),
          Redness: getRawScoreForPayload(analysisResults.Redness),
          Spots: getRawScoreForPayload(analysisResults.Spot),
          Dryness: getRawScoreForPayload(analysisResults.Dryness),
          Wrinkles: getRawScoreForPayload(analysisResults.Wrinkle),
          DarkCircles: getRawScoreForPayload(analysisResults.DarkCircle),
        };

        // Map last routine question answer to topN for recommendations
        const ROUTINE_QUESTION_ID = 'bdc5bed4-7a70-40ab-91b0-f5b86f16fea7';
        const ROUTINE_OPTION_TOPN: Record<string, number> = {
          '2a7c4dea-2cda-40c3-a8b4-28331df759e0': 3,
          '881cabef-7c01-4a48-82a1-8e7d0ac86311': 5,
          'd3637dc5-817e-4613-b033-33a55a56deb9': 6,
          'b29574e5-a0d1-430e-bcc7-579a58167c6f': 10,
        };

        let topN = 0;
        const routineAnswer = surveyAnswersForRecommendations.find(
          (a) => a.questionId === ROUTINE_QUESTION_ID
        );
        if (routineAnswer && routineAnswer.optionIds && routineAnswer.optionIds.length > 0) {
          const firstOptionId = routineAnswer.optionIds[0];
          if (ROUTINE_OPTION_TOPN[firstOptionId] !== undefined) {
            topN = ROUTINE_OPTION_TOPN[firstOptionId];
          }
        }

        const response = await surveyApi.computeRecommendations({
          analysisScores: analysisScoresPayload,
          surveyAnswers: surveyAnswersForRecommendations,
          topN,
          language: getRecommendationLanguage(i18n.language),
        });

        // Normalize products from response.results[]
        const productsData = Array.isArray(response?.results) ? response.results : [];

        setRecommendedProducts(productsData);
      } catch (err) {
        console.error('Failed to compute recommendations:', err);
      } finally {
        setIsLoadingRecommendations(false);
      }
    };

    fetchRecommendations();
  }, [analysisResults, surveyAnswersForRecommendations]);

  // Report compute: send analysis + overlays + image + products to report service (once we have data)
  useEffect(() => {
    if (!analysisResults || recommendedProducts === null) return;
    // Skip if we restored from sessionStorage and already have report data
    if (reportData && !location.state) return;

    // Prefer backend's processed 1024×1024 original image for the report.
    // Fall back to the cropped selfie from the camera preview when needed.
    const rawImage =
      (analysisImages?.original as string | undefined) ||
      (capturedImage?.replace(/^data:image\/\w+;base64,/, '') ?? '');
    if (!rawImage) return;

    // Report API expects complete base64 data URL
    const imageForReport = rawImage.startsWith('data:') ? rawImage : `data:image/png;base64,${rawImage}`;

    const analysisScoresPayload = {
      Acne: getRawScoreForPayload(analysisResults.Acne),
      Redness: getRawScoreForPayload(analysisResults.Redness),
      Spots: getRawScoreForPayload(analysisResults.Spot),
      Dryness: getRawScoreForPayload(analysisResults.Dryness),
      Wrinkles: getRawScoreForPayload(analysisResults.Wrinkle),
      DarkCircles: getRawScoreForPayload(analysisResults.DarkCircle),
    };

    const overlaysPayload = {
      Acne: analysisImages?.Acne ?? '',
      Redness: analysisImages?.Redness ?? '',
      Spots: analysisImages?.Spot ?? '',
      Dryness: analysisImages?.Dryness ?? '',
      Wrinkles: analysisImages?.Wrinkle ?? '',
      DarkCircles: analysisImages?.DarkCircle ?? '',
    };

    const productsPayload = (recommendedProducts ?? []).map((p: any) => ({
      productId: p.productId,
      title: p.title,
      shopifyProductId: p.shopifyProductId,
      score: p.score,
      rutinOnerisi: p.rutinOnerisi,
      images: p.images,
      variants: p.variants,
    }));

    reportApi
      .computeReport({
        userId: getVisitorId(),
        profileId: getReportProfileId(i18n.language),
        pdf: false,
        analysisScores: analysisScoresPayload,
        overlays: overlaysPayload,
        image: imageForReport,
        products: productsPayload,
        includeComparison: true,
        explain: true,
      })
      .then((data: any) => {
        if (data && (data.healthScore != null || data.level || data.summary || data.matrix || data.issues || data.recommendedProducts)) {
          setReportData({
            healthScore: data.healthScore,
            level: data.level,
            summary: data.summary,
            matrix: data.matrix,
            issues: data.issues,
            recommendedProducts: data.recommendedProducts ?? null,
          });
        }
      })
      .catch((err) => console.error('Report compute failed:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reportData/location.state omitted on purpose (restore vs fresh navigation)
  }, [analysisResults, analysisImages, capturedImage, recommendedProducts, i18n.language]);

  // Persist results to sessionStorage so refresh keeps report section and send-report area
  useEffect(() => {
    if (!reportData || !initialState || (!initialState.capturedImage && !initialState.analysisResults)) return;
    try {
      sessionStorage.setItem(
        RESULTS_PAGE_STORAGE_KEY,
        JSON.stringify({
          capturedImage: initialState.capturedImage,
          analysisImages: initialState.analysisImages,
          analysisResults: initialState.analysisResults,
          recommendedProducts,
          reportData,
        })
      );
    } catch (e) {
      console.warn('Failed to persist results page state:', e);
    }
  }, [reportData, recommendedProducts, initialState]);

  const handleSendReportEmail = async () => {
    if (!reportEmail || !analysisResults || recommendedProducts === null) return;

    const rawImage =
      (analysisImages?.original as string | undefined) ||
      (capturedImage?.replace(/^data:image\/\w+;base64,/, '') ?? '');
    if (!rawImage) return;

    const imageForReport = rawImage.startsWith('data:') ? rawImage : `data:image/png;base64,${rawImage}`;

    const analysisScoresPayload = {
      Acne: getRawScoreForPayload(analysisResults.Acne),
      Redness: getRawScoreForPayload(analysisResults.Redness),
      Spots: getRawScoreForPayload(analysisResults.Spot),
      Dryness: getRawScoreForPayload(analysisResults.Dryness),
      Wrinkles: getRawScoreForPayload(analysisResults.Wrinkle),
      DarkCircles: getRawScoreForPayload(analysisResults.DarkCircle),
    };

    const overlaysPayload = {
      Acne: analysisImages?.Acne ?? '',
      Redness: analysisImages?.Redness ?? '',
      Spots: analysisImages?.Spot ?? '',
      Dryness: analysisImages?.Dryness ?? '',
      Wrinkles: analysisImages?.Wrinkle ?? '',
      DarkCircles: analysisImages?.DarkCircle ?? '',
    };

    const productsPayload = (recommendedProducts ?? []).map((p: any) => ({
      productId: p.productId,
      title: p.title,
      shopifyProductId: p.shopifyProductId,
      score: p.score,
      rutinOnerisi: p.rutinOnerisi,
      images: p.images,
      variants: p.variants,
    }));

    try {
      setIsSendingReportEmail(true);
      await reportApi.sendReportEmail({
        userId: getVisitorId(),
        profileId: getReportProfileId(i18n.language),
        pdf: true,
        emailTo: reportEmail,
        analysisScores: analysisScoresPayload,
        overlays: overlaysPayload,
        image: imageForReport,
        products: productsPayload,
        includeComparison: true,
        explain: true,
      });
      toast.success(t('results.toastSent'));
    } catch (err) {
      console.error('Failed to send report email:', err);
      toast.error(t('results.toastSendFailed'));
    } finally {
      setIsSendingReportEmail(false);
    }
  };

  // Products list – use backend recommendations if available, otherwise fallback dummy list
  const products: Product[] = React.useMemo(
    () =>
      recommendedProducts && recommendedProducts.length > 0
        ? recommendedProducts.map((p, index) => {
            const rp =
              reportData?.recommendedProducts &&
              Array.isArray(reportData.recommendedProducts)
                ? reportData.recommendedProducts.find(
                    (r: any) =>
                      (r.productId && r.productId === p.productId) ||
                      (r.shopifyProductId && r.shopifyProductId === p.shopifyProductId)
                  )
                : null;
            const rutinSource = rp?.rutinOnerisi ?? p.rutinOnerisi;
            const englishDescription = rp?.rutinOnerisiEn ?? undefined;
            const routines: string[] = (() => {
              if (!rutinSource) return [];
              if (Array.isArray(rutinSource)) {
                return rutinSource as string[];
              }
              if (typeof rutinSource === 'string') {
                try {
                  const parsed = JSON.parse(rutinSource);
                  return Array.isArray(parsed) ? (parsed as string[]) : [];
                } catch {
                  return [];
                }
              }
              return [];
            })();

            return {
              id: p.productId ?? index,
              name: p.title ?? t('results.recommendedProduct'),
              brand: t('results.brandName'),
              description: getProductDescription(englishDescription, p.rutinOnerisi),
              imageUrl: Array.isArray(p.images) && p.images.length > 0 ? p.images[0].url : undefined,
              imageAlt: Array.isArray(p.images) && p.images.length > 0 ? p.images[0].altText ?? '' : '',
              url: p.handle ? `https://thepurestsolutions.com/products/${p.handle}` : undefined,
              routines,
            };
          })
        : [
          {
            id: 1,
            name: t('dummyProducts.p1name'),
            brand: t('results.brandName'),
            description: t('dummyProducts.p1desc'),
          },
          {
            id: 2,
            name: t('dummyProducts.p2name'),
            brand: t('results.brandName'),
            description: t('dummyProducts.p2desc'),
          },
          {
            id: 3,
            name: t('dummyProducts.p3name'),
            brand: t('results.brandName'),
            description: t('dummyProducts.p3desc'),
          },
        ],
    [recommendedProducts, reportData, t]
  );

  const routineTabs: string[] = React.useMemo(() => {
    const set = new Set<string>();
    products.forEach((p) => {
      (p.routines || []).forEach((r) => set.add(r));
    });
    return Array.from(set);
  }, [products]);

  const [selectedRoutine, setSelectedRoutine] = useState<string | 'All'>('All');

  const visibleProducts: Product[] =
    selectedRoutine === 'All'
      ? products
      : products.filter((p) => p.routines && p.routines.includes(selectedRoutine));

  // Radar chart options and series from report matrix (ApexCharts)
  const matrixRadarConfig = React.useMemo(() => {
    const matrix = reportData?.matrix;
    if (!matrix || Object.keys(matrix).length === 0) return null;
    const matrixI18nKeys = ['acne', 'redness', 'spots', 'dryness', 'wrinkles', 'darkCircles'] as const;
    const categories = matrixI18nKeys.map((k) => t(`results.matrix.${k}`));
    const keys: (keyof ReportMatrix)[] = ['Acne', 'Redness', 'Spots', 'Dryness', 'Wrinkles', 'DarkCircles'];
    // UI normalization: Final = 100 - (value * 100), where incoming value is 0–1
    const data = keys.map((k) => {
      const raw = matrix[k];
      if (raw == null) return 0;
      const v = Number(raw);
      return 100 - v * 100;
    });
    const max = Math.max(10, ...data);
    const options: ApexOptions = {
      chart: { type: 'radar' },
      xaxis: { categories },
      yaxis: { min: 0, max: Math.ceil(max * 1.1) },
      stroke: { width: 2 },
      fill: { opacity: 0.2 },
      markers: { size: 4 },
      plotOptions: { radar: { polygons: { strokeColors: '#e0e0e0', connectorColors: '#e0e0e0' } } },
      colors: ['#6b2d9e'],
      dataLabels: {
        enabled: true,
        formatter: (val: number) => (val != null ? Number(val).toFixed(2) : ''),
      },
      legend: { show: false },
    };
    const series = [{ name: t('results.radarSeries'), data }];
    return { options, series };
  }, [reportData?.matrix, t]);

  return (
    <div className="results-page">
      <div className="results-container">
        <div className="results-left">
          <div className="image-section">
            {baseImageSrc ? (
              <div className="result-image-stack">
                <img src={baseImageSrc} alt={t('results.originalAlt')} className="result-image base" />
                {overlayOrder.map((key) => {
                  const src = getDataUrl(analysisImages?.[key]);
                  if (!src || hiddenOverlayKeys.has(key)) return null;
                  const overlayMatrixKey: Record<string, 'acne' | 'redness' | 'spots' | 'dryness' | 'wrinkles' | 'darkCircles'> = {
                    Acne: 'acne',
                    Redness: 'redness',
                    Spot: 'spots',
                    Dryness: 'dryness',
                    Wrinkle: 'wrinkles',
                    DarkCircle: 'darkCircles',
                  };
                  const mk = overlayMatrixKey[key] ?? 'acne';
                  return (
                    <img
                      key={key}
                      src={src}
                      alt={t('results.overlayAlt', { key: t(`results.matrix.${mk}`) })}
                      className="result-image overlay"
                    />
                  );
                })}
              </div>
            ) : (
              <div className="image-placeholder">{t('results.noImage')}</div>
            )}
          </div>
          
          <div className="metrics-section">
            <h3 className="metrics-title">{t('results.skinRatingsTitle')}</h3>
            <p className="metrics-toggle-hint">{t('results.toggleOverlayHint')}</p>
            <div className="metrics-grid">
              {metrics.map((metric, index) => {
                const overlayKey = METRIC_NAME_KEY_TO_OVERLAY[metric.nameKey];
                const overlayRaw = overlayKey ? analysisImages?.[overlayKey] : undefined;
                const hasToggleOverlay = Boolean(overlayRaw && getDataUrl(overlayRaw));
                const overlayHidden = overlayKey ? hiddenOverlayKeys.has(overlayKey) : false;
                return (
                  <div
                    key={index}
                    className={`metric-card metric-${metric.level}${
                      hasToggleOverlay ? ' metric-card--toggleable' : ''
                    }${overlayHidden ? ' metric-card--overlay-hidden' : ''}`}
                    {...(hasToggleOverlay
                      ? {
                          role: 'button' as const,
                          tabIndex: 0,
                          'aria-pressed': !overlayHidden,
                          title: t('results.toggleOverlayHint'),
                          onClick: () => toggleOverlayForMetric(metric.nameKey),
                          onKeyDown: (e: React.KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleOverlayForMetric(metric.nameKey);
                            }
                          },
                        }
                      : {})}
                  >
                    <div className="metric-name">{t(`results.metrics.${metric.nameKey}`)}</div>
                    <div className="metric-rating">
                      {t(`results.level.${levelCssToI18nKey(metric.level)}`)}
                    </div>
                    <div className="metric-score">
                      {t('results.scoreLine', { value: (100 - metric.score).toFixed(2) })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="results-right">
          <div className={`level-section level-${level}`}>
            <h3 className="level-title">{t('results.levelTitle')}</h3>
            <div className="level-bar">
              {LEVEL_CSS_ORDER.map((seg) => (
                <div
                  key={seg}
                  className={`level-segment ${level === seg ? 'active' : ''}`}
                >
                  {t(`results.level.${levelCssToI18nKey(seg)}`)}
                </div>
              ))}
            </div>
            <div className="level-indicator">
              {t(`results.level.${levelCssToI18nKey(level)}`)}
            </div>
            <div className="level-average">
              {t('results.averageScore', { value: (100 - averageScore).toFixed(2) })}
            </div>
          </div>

          {/* Send report by email – prominent at top of right column so users see it */}
          {reportData && (reportData.healthScore != null || reportData.level || reportData.summary || reportData.matrix) && (
            <div className="send-report-section">
              <h3 className="send-report-title">{t('results.sendReportTitle')}</h3>
              <p className="send-report-desc">{t('results.sendReportDesc')}</p>
              <div className="report-email">
                <input
                  type="email"
                  className="report-email-input"
                  placeholder={t('results.emailPlaceholder')}
                  value={reportEmail}
                  onChange={(e) => setReportEmail(e.target.value)}
                />
                <button
                  className="report-email-button"
                  onClick={handleSendReportEmail}
                  disabled={!reportEmail || isSendingReportEmail}
                >
                  {isSendingReportEmail ? t('results.sending') : t('results.sendReport')}
                </button>
              </div>
            </div>
          )}

          {reportData && (reportData.healthScore != null || reportData.level || reportData.summary || reportData.matrix) && (
            <div className="report-section">
              <h3 className="report-title">{t('results.reportTitle')}</h3>
              {reportData.matrix && Object.keys(reportData.matrix).length > 0 && (
                <div className="report-matrix">
                  <h4 className="report-matrix-title">{t('results.skinMapTitle')}</h4>
                  {matrixRadarConfig && (
                    <div className="report-matrix-radar">
                      <ReactApexChart
                        options={matrixRadarConfig.options}
                        series={matrixRadarConfig.series}
                        type="radar"
                        height={320}
                      />
                    </div>
                  )}
                  <div className="report-matrix-grid">
                    {(
                      [
                        { key: 'Acne', labelKey: 'acne' as const },
                        { key: 'Redness', labelKey: 'redness' as const },
                        { key: 'Spots', labelKey: 'spots' as const },
                        { key: 'Dryness', labelKey: 'dryness' as const },
                        { key: 'Wrinkles', labelKey: 'wrinkles' as const },
                        { key: 'DarkCircles', labelKey: 'darkCircles' as const },
                      ] as const
                    ).map(({ key, labelKey }) => {
                      const label = t(`results.matrix.${labelKey}`);
                      const raw = reportData.matrix![key as keyof ReportMatrix];
                      if (raw == null) return null;
                      const value = 100 - Number(raw) * 100;
                      const clamped = Math.max(0, Math.min(100, value));
                      const issue = reportData.issues?.find(
                        (i) => i.modelType === key
                      );
                      let barColor = '#16a34a'; // default dark green
                      if (clamped <= 25) {
                        barColor = '#ef4444'; // red
                      } else if (clamped <= 50) {
                        barColor = '#f97316'; // orange
                      } else if (clamped <= 75) {
                        barColor = '#eab308'; // yellow
                      } else if (clamped <= 90) {
                        barColor = '#4ade80'; // light green
                      } else {
                        barColor = '#16a34a'; // dark green
                      }
                      return (
                        <div key={key} className="report-matrix-item">
                          <div className="report-matrix-label">{label}</div>
                          {issue?.title && (
                            <div className="report-matrix-issue-title">
                              {issue.title}
                            </div>
                          )}
                          <div className="report-matrix-progress">
                            <div
                              className="report-matrix-progress-fill"
                              style={{ width: `${clamped}%`, backgroundColor: barColor }}
                            />
                          </div>
                          {issue && (
                            <div className="report-matrix-issue-row">
                              <span className="report-matrix-issue-severity">
                                {issue.severity}
                              </span>
                              <button
                                type="button"
                                className="report-matrix-issue-help"
                                onClick={() =>
                                  setOpenIssueKey(
                                    openIssueKey === issue.key ? null : issue.key
                                  )
                                }
                              >
                                ?
                              </button>
                              {openIssueKey === issue.key && (
                                <div className="report-matrix-issue-popup">
                                  {issue.description && (
                                    <div className="report-matrix-issue-description">
                                      {issue.description}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                          <div className="report-matrix-value">
                            {Number(value).toFixed(0)}/100
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="products-section">
            <div className="products-header">
              <h3 className="products-title">{t('results.routineTitle')}</h3>
            </div>

            {routineTabs.length > 0 && (
              <div className="routine-tabs">
                <button
                  type="button"
                  className={`routine-tab ${selectedRoutine === 'All' ? 'active' : ''}`}
                  onClick={() => setSelectedRoutine('All')}
                >
                  {t('results.allRoutines')}
                </button>
                {routineTabs.map((r) => (
                  <button
                    key={r}
                    type="button"
                    className={`routine-tab ${selectedRoutine === r ? 'active' : ''}`}
                    onClick={() => setSelectedRoutine(r)}
                  >
                    {r}
                  </button>
                ))}
              </div>
            )}

      
            <div className="products-list">
              {visibleProducts.map((product) => (
                <div key={product.id} className="product-card">
                  <>
                    <div className="product-image-container">
                      {product.imageUrl ? (
                        <img
                          src={product.imageUrl}
                          alt={product.imageAlt || product.name}
                          className="product-image"
                        />
                      ) : (
                        <div className="product-image-placeholder">
                          {product.brand}
                        </div>
                      )}
                    </div>
                    <div className="product-info">
                      <div className="product-brand">{product.brand}</div>
                      <div className="product-name">{product.name}</div>
                      {product.description && (
                        <div className="product-description">{product.description}</div>
                      )}
                      {product.routines && product.routines.length > 0 && (
                        <div className="product-routines">
                          {product.routines.map((r) => (
                            <span key={r} className="product-routine-badge">
                              {r}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="results-footer">
        <button className="restart-button" onClick={handleRestart}>
          {t('results.restart')}
        </button>
      </div>
    </div>
  );
};

export default ResultsPage;
